import { type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2, LogOut, ShieldAlert } from "lucide-react";
import { useAuthStore } from "@/services/authStore";
import type { UserRole } from "@/types/auth";

export interface RequireAuthProps {
  /** Optional whitelist of roles allowed past the guard. */
  roles?: UserRole[];
  children: ReactNode;
}

/**
 * Route guard component.
 *
 * Behaviour:
 *   - While the auth store is still initializing → full-screen spinner.
 *   - When signed out → redirect to /signin?next=<current path>.
 *   - When signed in but no profile row in `public.users` → Forbidden screen
 *     (auth user exists but hasn't been promoted to a platform role yet).
 *   - When signed in with the wrong role → Forbidden screen.
 *   - Otherwise → render children.
 */
export function RequireAuth({ roles, children }: RequireAuthProps) {
  const status = useAuthStore((s) => s.status);
  const profile = useAuthStore((s) => s.profile);
  const profileLoading = useAuthStore((s) => s.profileLoading);
  const location = useLocation();

  // Still booting auth, or signed in but the profile fetch is in flight —
  // show a spinner rather than flashing the "not provisioned" screen.
  if (status === "initializing" || (status === "signed_in" && profileLoading && !profile)) {
    return (
      <div className="app-ambient flex min-h-screen items-center justify-center bg-background text-zinc-400 light:text-zinc-600">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  if (status === "signed_out") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signin?next=${next}`} replace />;
  }

  if (!profile) {
    return (
      <Forbidden
        title="Account not provisioned"
        message="You're signed in to Supabase Auth but you don't have a row in the platform users table yet. Ask a super-admin to promote your account."
      />
    );
  }

  if (roles && !roles.includes(profile.role)) {
    return (
      <Forbidden
        title="Access denied"
        message={`This page is restricted to ${roles.join(" or ")}. Your role is "${profile.role}".`}
      />
    );
  }

  return <>{children}</>;
}

function Forbidden({ title, message }: { title: string; message: string }) {
  const profile = useAuthStore((s) => s.profile);
  const signOut = useAuthStore((s) => s.signOut);
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate("/signin", { replace: true });
  }

  return (
    <main className="app-ambient flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="max-w-md rounded-2xl border border-zinc-800 light:border-black/10 bg-zinc-900/60 light:bg-white/70 p-8 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-amber-400" aria-hidden />
        <h1 className="mt-4 text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-zinc-400 light:text-zinc-600">{message}</p>

        {profile && (
          <p className="mt-4 text-xs text-dim light:text-zinc-600">
            Signed in as <span className="font-mono text-zinc-300 light:text-zinc-700">{profile.email}</span>{" "}
            ({profile.role})
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <a
            href="/admin"
            className="inline-flex items-center justify-center rounded-md border border-zinc-700 light:border-black/20 px-4 py-2 text-sm font-medium text-zinc-200 light:text-zinc-800 transition hover:border-zinc-500 light:hover:border-black/30"
          >
            ← Back to Admin
          </a>
          <a
            href="/app"
            className="inline-flex items-center justify-center rounded-md border border-zinc-800 light:border-black/10 bg-zinc-950/50 light:bg-white/60 px-4 py-2 text-sm font-medium text-zinc-300 light:text-zinc-700 transition hover:border-zinc-600 light:hover:border-black/25"
          >
            Consumer feed
          </a>
          {profile && (
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-zinc-700 light:border-black/20 bg-zinc-900/60 light:bg-white/70 px-4 py-2 text-sm font-medium text-zinc-200 light:text-zinc-800 transition hover:border-zinc-500 light:hover:border-black/30"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden />
              Sign out
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
